import { loadTossPayments, ANONYMOUS } from "@tosspayments/tosspayments-sdk";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

const clientKey = import.meta.env.VITE_WIDGET_CLIENT_KEY;
if (!clientKey) {
  throw new Error("Toss Payments client key is not configured");
}

interface PaymentProps {
  amount: number;
  orderId: string;
  orderName: string;
  customerKey?: string;
  successUrl: string;
  failUrl: string;
  customerEmail?: string;
}

export const Payment: React.FC<PaymentProps> = ({
  amount: initialAmount, // props initialAmount
  orderId,
  orderName,
  customerKey: propCustomerKey,
  successUrl,
  failUrl,
  customerEmail,
}) => {
  const [widgets, setWidgets] = useState<TossWidgetsInstance | null>(null);
  const [ready, setReady] = useState(false);
  const [currentAmount, setCurrentAmount] = useState(initialAmount);

  useEffect(() => {
    const initializeWidgets = async () => {
      try {
        const tossPayments = await loadTossPayments(clientKey);
        const currentCustomerKey = propCustomerKey || ANONYMOUS;

        const widgetsInstance = tossPayments.widgets({
          customerKey: currentCustomerKey,
        })
        setWidgets(widgetsInstance);
      } catch (error) {
        console.error("Failed to initialize Tosspayments or Widgets", error);
        toast.error("결제 모듈 초기화에 실패했습니다.");
      }
    };

    initializeWidgets();
  }, [clientKey, propCustomerKey]);

  useEffect(() => {
    const renderPaymentUI = async () => {
      if (widgets == null) return;
      try {
        await widgets.setAmount({ currency: "KRW", value: currentAmount });
        await Promise.all([
          widgets.renderPaymentMethods({
            selector: "#payment-method",
            variantKey: "DEFAULT",
          }),
          widgets.renderAgreement({
            selector: "#agreement",
            variantKey: "AGREEMENT",
          }),
        ]);
        setReady(true);
      } catch (error) {
        console.error("Failed to render payment UI", error);
        toast.error("결제 UI를 표시하는 중 오류가 발생했습니다.");
        setReady(false);
      }
    };
    renderPaymentUI();
  }, [widgets, currentAmount]);

  const handlePaymentRequest = async () => {
    if (widgets == null || !ready) {
      toast.error("결제 위젯이 아직 준비되지 않았습니다.");
      return;
    }

    try {
      console.log("Requesting payment with successUrl:", successUrl);
      console.log("Requesting payment with failUrl:", failUrl);
      // ------ '결제하기' 버튼 누르면 결제창 띄우기 ------
      // successUrl, failUrl 등은 props로 받은 값을 사용합니다.
      await widgets.requestPayment({
        orderId: orderId,
        orderName: orderName,
        successUrl: successUrl,
        failUrl: failUrl,
        customerEmail: customerEmail,
        // customerMobilePhone: customerMobilePhone,
      });
    } catch (error) {
      console.error("Payment request error:", error);
      toast.error(`결제 요청 중 오류가 발생했습니다.`);
    }
  };

  if (!widgets) {
    return <div className="p-4 text-center">결제 정보를 불러오는 중입니다...</div>
  }

  return (
    <div className="payment-widget-container space-y-4 w-full">
      <div id="payment-method" />
      <div id="agreement" />

      <button
        className="w-full py-3.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:cursor-not-allowed flex items-center justify-center text-base"
        disabled={!ready}
        onClick={handlePaymentRequest}
      >
        {currentAmount} 원 결제하기
      </button>
    </div>
  )
}